import React, { useState, useEffect } from "react";
import {
  Button, Container, Row, Col, Spinner
} from 'react-bootstrap';
import {
  FacebookShareButton, TwitterShareButton, PinterestShareButton
} from "react-share";
import {
  FaFacebook, FaXTwitter, FaPinterest, FaEnvelope, FaRegCopy, FaInstagram, FaPalette
} from "react-icons/fa6"; // FontAwesome icons
import { toast } from "react-toastify"; // Toast notifications
import "react-toastify/dist/ReactToastify.css"; // Toast styles
import { formatCurrency } from '../../../utilities/formatCurrency';
import useProductApi, { Product } from '../../../managers/ProductManager';
import useUserApi, { User } from '../../../managers/UserManager';
import { useShoppingCart } from "../../../context/ShoppingCartContext";
import Loading, { LoadingStates } from '../../../components/Loading';
import { getBaseAppUrl } from "../../../managers/EnvironmentManager";
import useSiteApi from "../../../managers/SiteManager";
import { useNavigate } from 'react-router-dom';
import useCatalogApi, { CatalogItem } from "../../../managers/CatalogManager";
import CatalogItemSelector from "./CatalogItemSelector";
import { useAuth0 } from "@auth0/auth0-react";
import useImageApi from "../../../managers/ImageManager";
import ProductActions from "./ProductActions";

// Creative loading messages for product creation
const CREATION_MESSAGES = [
  "Bringing your vision to life...",
  "Extending your artwork for the new format...",
  "AI is crafting your masterpiece...",
  "Transforming pixels into perfection...",
  "Creating something beautiful just for you...",
  "Adapting your design to the new dimensions...",
  "Weaving creativity with AI...",
  "Watch your ideas bloom into reality...",
  "Generating your personalized product...",
  "Almost there... finalizing your creation..."
];


interface ProductDetailProps {
  product: Product;
}

const ProductDetail: React.FC<ProductDetailProps> = ({ product }) => {

  const navigate = useNavigate();
  const { getCatalogItems } = useCatalogApi();
  const { getProductUrl, generateProduct, getProduct } = useProductApi();
  const { getCurrentUser } = useUserApi();
  const { getSiteName } = useSiteApi();
  const { isAuthenticated, isLoading, loginWithRedirect, user } = useAuth0();
  const { generateImageAsync, getImageBaseUrl } = useImageApi();

  const {
    getItemQuantity,
    addToCart,
    removeFromCart,
    openCart,
  } = useShoppingCart();

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [showFullPrompt, setShowFullPrompt] = useState(false);
  const [catalogItems, setCatalogItems] = useState<CatalogItem[]>([]);
  const [selectedCatalogItemId, setSelectedCatalogItemId] = useState("");
  const [selectedVariantId, setSelectedVariantId] = useState("");

  const handleSelectVariant = (variantId: string) => {
    setSelectedVariantId(variantId);
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingState, setLoadingState] = useState(LoadingStates.SUCCESS);
  const [loadingMessage, setLoadingMessage] = useState("Creating product...");
  const [messageIndex, setMessageIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  useEffect(() => {
    const fetchCatalog = async () => {
      setLoading(true);
      try {
        const res = await getCatalogItems();
        // Include all different product types, and include current product type if it has multiple variants
        const filtered = res.items.filter(item =>
          item.id !== product.catalogItemId || // Different product types
          item?.variants?.length > 1 // Or same product type with multiple variants
        );
        setCatalogItems(filtered);
        setError(null);
      } catch (e) {
        console.error("Failed to load catalog", e);
        setError("Unable to load catalog.");
      } finally {
        setLoading(false);
      }
    };
    fetchCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.catalogItemId]);

  // Track the previous catalog item to know when we're switching items
  const [previousCatalogItemId, setPreviousCatalogItemId] = useState("");

  useEffect(() => {
    const selectedItem = catalogItems.find(item => item.id === selectedCatalogItemId);

    if (selectedItem) {
      // For different product types, show all variants
      // For same product type, exclude the current variant
      const filtered = selectedItem.id === product.catalogItemId
        ? selectedItem.variants.filter(variant => variant.id !== product.catalogItemVariantId)
        : selectedItem.variants;


      // Auto-select variant if there's only one option
      if (filtered.length === 1) {
        setSelectedVariantId(filtered[0].id);
      } else if (filtered.length === 0) {
        setSelectedVariantId("");
      } else {
        // Multiple options - only clear when switching to a different catalog item
        // This preserves variants selected by CatalogItemSelector for the same item
        if (selectedCatalogItemId !== previousCatalogItemId) {
          setSelectedVariantId("");
        }
      }
    } else {
      setSelectedVariantId("");
    }

    // Update the previous catalog item ID
    setPreviousCatalogItemId(selectedCatalogItemId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCatalogItemId, product.catalogItemId, product.catalogItemVariantId]);

  // Cycle through creation messages every 10 seconds while loading
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (loadingState === LoadingStates.LOADING) {
      interval = setInterval(() => {
        setMessageIndex((prevIndex) => {
          const newIndex = (prevIndex + 1) % CREATION_MESSAGES.length;
          setLoadingMessage(CREATION_MESSAGES[newIndex]);
          return newIndex;
        });
      }, 10000); // 10 seconds
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [loadingState]);

  // Fetch current user
  useEffect(() => {
    if (!isLoading) {
      getCurrentUser()
        .then(setCurrentUser)
        .catch((err) => {
          console.error("Failed to fetch current user:", err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  if (!product) return <p>Product not found</p>;

  const handleCreateDifferentProduct = async () => {
    // Validate that this product has a source image
    if (!product.sourceImageId) {
      alert('This product cannot be used to create other products because it is missing source image data. Please try with a different product.');
      return;
    }

    // Start with a random creative message
    const randomIndex = Math.floor(Math.random() * CREATION_MESSAGES.length);
    setMessageIndex(randomIndex);
    setLoadingMessage(CREATION_MESSAGES[randomIndex]);
    setLoadingState(LoadingStates.LOADING);

    try {
      console.log('Creating different product with:', {
        sourceImageId: product.sourceImageId,
        sourceImageUrls: product.previewImageUrls,
        catalogItemId: selectedCatalogItemId,
        catalogItemVariantId: selectedVariantId
      });

      // Get the selected catalog item and variant info
      const selectedCatalogItem = catalogItems.find(item => item.id === selectedCatalogItemId);
      const selectedVariant = selectedCatalogItem?.variants?.find(variant => variant.id === selectedVariantId);

      if (!selectedCatalogItem || !selectedVariant) {
        throw new Error('Selected catalog item or variant not found');
      }

      // Get current product's catalog variant to compare orientations
      const currentCatalogItem = catalogItems.find(item => item.id === product.catalogItemId) ||
        await getCatalogItems().then(res => res.items?.find(item => item.id === product.catalogItemId));
      const currentVariant = currentCatalogItem?.variants?.find(variant => variant.id === product.catalogItemVariantId);

      // Determine orientations
      const currentOrientation = currentVariant?.orientation || product.catalogItemVariant?.orientation || 'square';
      const targetOrientation = selectedVariant.orientation || 'square';

      console.log('Orientation comparison:', {
        currentCatalogItemId: product.catalogItemId,
        currentVariantId: product.catalogItemVariantId,
        currentOrientation,
        selectedCatalogItemId,
        selectedVariantId,
        targetOrientation,
        orientationsMatch: currentOrientation === targetOrientation
      });

      // Get the source image URL from the original source image, not the product preview
      if (!product.sourceImageId) {
        throw new Error('Source image ID not found');
      }

      // Clean sourceImageId to prevent double slashes in URL
      // sourceImageId is the image UUID, imageKey format is ${imageId}.png
      const cleanSourceImageId = product.sourceImageId.startsWith('/')
        ? product.sourceImageId.substring(1)
        : product.sourceImageId;
      const sourceImageUrl = `${getImageBaseUrl()}/${cleanSourceImageId}.png`;

      console.log('Using source image URL:', sourceImageUrl, 'instead of preview URL:', product.previewImageUrls?.[0]);

      // Additional validation to ensure we have a prompt if we need to generate
      if (currentOrientation !== targetOrientation && !product.prompt?.trim()) {
        console.warn('No original prompt available for orientation conversion. Product details:', {
          productName: product.name,
          productId: product.productId,
          hasPrompt: !!product.prompt,
          promptLength: product.prompt?.length || 0
        });
      }

      let newProduct;

      if (currentOrientation === targetOrientation) {
        // Same orientation: Use existing image directly (fast path ~30 seconds)
        console.log(`Orientations match (${currentOrientation}). Using existing image directly.`);
        setLoadingMessage("Creating product with existing image...");

        newProduct = await generateProduct({
          userId: user?.sub || "0",
          sourceImageUrl: sourceImageUrl,
          sourceImageId: product.sourceImageId!,
          catalogItemId: selectedCatalogItemId,
          catalogItemVariantId: selectedVariantId
        });

      } else {
        // Different orientation: Generate new image then create product (slow path)
        console.log(`Orientation mismatch (${currentOrientation} → ${targetOrientation}). Generating new image.`);

        // Step 1: Generate new image with target orientation
        setLoadingMessage("Generating image for new product format...");

        // Use a specific prompt optimized for orientation conversion instead of the original creation prompt
        const promptToUse = `Keep this exact image design and extend it seamlessly to fit ${selectedVariant.orientation || 'square'} orientation. Preserve all existing elements and fill any new space with matching background, patterns, or complementary elements that blend naturally with the current composition.`;

        console.log('Using orientation conversion prompt instead of original prompt:', promptToUse);
        console.log('Original creation prompt was:', product.prompt);

        const newImage = await generateImageAsync({
          userId: user?.sub || "0",
          action: 'generate-from-source',
          prompt: promptToUse,
          vendorId: 'openai',
          imageModel: 'gpt-image-2',
          imageShape: selectedVariant.orientation || 'square',
          imageStyle: 'vivid',
          sourceImageUrl: sourceImageUrl,
          imageName: product.name,
          imageTags: []
        });

        console.log('Image generation result:', newImage);

        if (!newImage?.imageUrl) {
          // If status is completed but imageUrl is missing, try to construct it
          if (newImage?.status === 'completed' && newImage?.imageKey) {
            // Clean imageKey to prevent double slashes in URL
            const cleanImageKey = newImage.imageKey.startsWith('/')
              ? newImage.imageKey.substring(1)
              : newImage.imageKey;
            newImage.imageUrl = `${getImageBaseUrl()}/${cleanImageKey}`;
            console.log('Constructed imageUrl from imageKey:', newImage.imageUrl);
          } else {
            throw new Error(`Image generation failed. Status: ${newImage?.status || 'unknown'}, Error: ${newImage?.errorMessage || 'No error message'}`);
          }
        }

        console.log('Image generated successfully:', {
          imageId: newImage.imageId,
          imageUrl: newImage.imageUrl,
          targetOrientation: selectedVariant.orientation
        });

        // Step 2: Create product with the completed image
        setLoadingMessage("Creating product...");

        // Small delay to help with eventual consistency issues
        await new Promise(resolve => setTimeout(resolve, 500));

        newProduct = await generateProduct({
          userId: user?.sub || "0",
          sourceImageUrl: newImage.imageUrl,
          sourceImageId: newImage.imageId,
          catalogItemId: selectedCatalogItemId,
          catalogItemVariantId: selectedVariantId
        });
      }

      console.log('Product generated successfully:', {
        productId: newProduct.productId,
        previewImageUrls: newProduct.previewImageUrls
      });

      if (!newProduct?.previewImageUrls?.[0]) {
        throw new Error('Product preview failed.');
      }

      setLoadingState(LoadingStates.SUCCESS);
      toast.success("New product created!");

      // Navigate to the new product
      const productUrl = `/products/${selectedCatalogItemId}/${selectedCatalogItem?.category || 'uncategorized'}/${newProduct.productId}`;
      console.log('Redirecting to:', productUrl);
      navigate(productUrl);

    } catch (err: any) {
      console.error("Create failed", err);
      console.error("Full error object:", JSON.stringify(err, null, 2));

      setLoadingMessage(err.message || "Failed to create product");
      setLoadingState(LoadingStates.ERROR);

      // Show more specific error message
      if (err.message?.includes('orientation')) {
        toast.error("Failed to generate image for the selected orientation. Please try again.");
      } else if (err.message?.includes('not ready')) {
        toast.error("Source image is still being processed. Please wait a moment and try again.");
      } else if (err.message?.includes('timeout')) {
        toast.error("Request timed out. The new product might still be generating. Please check back in a moment.");
      } else {
        toast.error(err.message || "Could not create new product. Please check the console for details.");
      }
    }
  };

  const getTruncatedPrompt = () => {
    if (!product?.prompt) return '';
    return product.prompt.length > 250 && !showFullPrompt
      ? product.prompt.slice(0, 250) + "..."
      : product.prompt;
  };

  const handleRemixProduct = () => {
    console.log('[handleRemixProduct] Button clicked');
    try {
      if (!product.sourceImageId) {
        console.error('[handleRemixProduct] No sourceImageId found');
        toast.error('This product cannot be remixed because it is missing source image data.');
        return;
      }

      // Clean sourceImageId to prevent double slashes in URL
      // sourceImageId is the image UUID, imageKey format is ${imageId}.png
      const cleanSourceImageId = product.sourceImageId.startsWith('/')
        ? product.sourceImageId.substring(1)
        : product.sourceImageId;
      const sourceImageUrl = `${getImageBaseUrl()}/${cleanSourceImageId}.png`;

      console.log('[handleRemixProduct] Navigating to remix page with:', {
        sourceImageUrl,
        remixSourceImageId: product.sourceImageId,
        catalogItemId: product.catalogItemId,
        catalogItemVariantId: product.catalogItemVariantId,
        originalPrompt: product.prompt
      });

      navigate("/products/remix", {
        state: {
          remixSourceImageUrl: sourceImageUrl,
          remixSourceImageId: product.sourceImageId,
          productPreviewUrl: product.previewImageUrls?.[0] || sourceImageUrl,
          catalogItemId: product.catalogItemId,
          catalogItemVariantId: product.catalogItemVariantId,
          originalPrompt: product.prompt ?? "",
          originalProductName: product.name ?? "Product",
          originalTags: product.tags ?? [],
          _k: Date.now(), // force a new location entry
        } as const,
      });

      console.log('[handleRemixProduct] Navigation called successfully');
    } catch (err) {
      console.error("[handleRemixProduct] Failed to remix product:", err);
      toast.error("Failed to remix product.");
    }
  };

  const handleAddToCart = async (product: Product) => {
    addToCart({
      productId: product?.productId,
      productUserId: product?.userId,
      name: product.name,
      description: product.catalogItemFullName,
      catalogItemId: product?.catalogItemId || '0',
      catalogItemVariantId: product?.catalogItemVariantId || '0',
      price: product?.price,
      quantity: 1,
      metadata: { previewImageUrls: product?.previewImageUrls },
    });
    openCart();
  }

  const siteName = getSiteName();
  // Get the catalog item to pass its category to getProductUrl
  const productCatalogItem = catalogItems.find(item => item.id === product.catalogItemId);
  const productUrl = getProductUrl(product, productCatalogItem?.category) || '';
  const previewImages = product?.previewImageUrls || [];
  const productImgUrl = previewImages[currentImageIndex] || previewImages[0] || `${getBaseAppUrl()}/default-placeholder.png`;
  const quantity = product.productId ? getItemQuantity(product?.productId) : 0;

  // Minimum swipe distance (in px) to trigger navigation
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentImageIndex < previewImages.length - 1) {
      setCurrentImageIndex(prev => prev + 1);
    }
    if (isRightSwipe && currentImageIndex > 0) {
      setCurrentImageIndex(prev => prev - 1);
    }
  };

  const handlePrevImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(prev => prev - 1);
    }
  };

  const handleNextImage = () => {
    if (currentImageIndex < previewImages.length - 1) {
      setCurrentImageIndex(prev => prev + 1);
    }
  };
  // Construct mailto link for email sharing
  const mailtoLink = `mailto:?subject=${encodeURIComponent(
    `Check out this product from ${siteName}`
  )}&body=${encodeURIComponent(
    `Hey,\n\nI found this amazing product on ${siteName} and thought you'd like it!\n\nCheck it out: ${productUrl}\n\nBest,\n[Your Name]`
  )}`;
  const SHARE_ICON_SIZE = 26;

  // Function to Copy Link to Clipboard with Toast
  const copyToClipboard = async (message: string) => {
    try {
      await navigator.clipboard.writeText(productUrl);
      toast.success(message, {
        position: "bottom-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      console.error("Failed to copy link:", error);
      toast.error("Failed to copy link.");
    }
  };

  return (
    <>
      {/* Overlay Loading Screen */}
      {loadingState === LoadingStates.LOADING && (
        <Loading
          state={loadingState}
          message={loadingMessage}
          subtitle="Please don't close this window. We're creating something amazing for you!"
          overlay={true}
          image={productImgUrl}
        />
      )}

      <Container>
        <Row className="justify-content-center">
          <Col xs={12} md={7} className="text-center">
            {/* Image Carousel */}
            <div
              style={{ position: 'relative', userSelect: 'none' }}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <img
                src={productImgUrl}
                alt="Generated Product"
                className="img-fluid"
                style={{ maxWidth: '100%', objectFit: 'contain', borderRadius: "8px" }}
              />

              {/* Navigation Arrows (only show if multiple images) */}
              {previewImages.length > 1 && (
                <>
                  <Button
                    variant="light"
                    onClick={handlePrevImage}
                    disabled={currentImageIndex === 0}
                    style={{
                      position: 'absolute',
                      left: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      opacity: currentImageIndex === 0 ? 0.3 : 0.8,
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    ‹
                  </Button>
                  <Button
                    variant="light"
                    onClick={handleNextImage}
                    disabled={currentImageIndex === previewImages.length - 1}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      opacity: currentImageIndex === previewImages.length - 1 ? 0.3 : 0.8,
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    ›
                  </Button>
                </>
              )}
            </div>

            {/* Thumbnail Navigation (only show if multiple images) */}
            {previewImages.length > 1 && (
              <div className="d-flex justify-content-center mt-3 gap-2" style={{ flexWrap: 'wrap' }}>
                {previewImages.map((imageUrl, index) => (
                  <img
                    key={index}
                    src={imageUrl}
                    alt={`Preview ${index + 1}`}
                    onClick={() => setCurrentImageIndex(index)}
                    style={{
                      width: '60px',
                      height: '60px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      border: currentImageIndex === index ? '3px solid #0d6efd' : '3px solid transparent',
                      opacity: currentImageIndex === index ? 1 : 0.6,
                      transition: 'all 0.2s ease'
                    }}
                  />
                ))}
              </div>
            )}

            {/* Image Counter (only show if multiple images) */}
            {previewImages.length > 1 && (
              <div className="text-muted mt-2" style={{ fontSize: '0.9rem' }}>
                {currentImageIndex + 1} / {previewImages.length}
              </div>
            )}
          </Col>
          <Col xs={12} md={5}>
            <div className="d-flex align-items-center gap-2 mb-2">
              <h3 className="mb-0">{product?.name}</h3>
            </div>
            <p className="fw-bold">{product?.catalogItemFullName || product?.catalogItemName}</p>
            <span className="fw-bold mt-3">{formatCurrency(product?.price)}</span>
            <div className="mt-3">
              {product?.status === "DISCONTINUED" || product.catalogItemVariant?.status === "DISCONTINUED" ? (
                <Button className="w-100" variant="secondary" disabled title="This product has been discontinued">
                  Discontinued
                </Button>
              ) : quantity === 0 ? (
                <Button variant="primary" className="w-100" onClick={() => handleAddToCart(product)}>
                  + Add To Cart
                </Button>
              ) : (
                <Button
                  onClick={() => removeFromCart(product?.productId || "")}
                  className="w-100"
                  variant="secondary"
                >
                  - Remove From Cart
                </Button>
              )}
            </div>


            <div className="my-3">

              {/* Instagram Share Button (Copies Link) */}
              <Button
                title="Copy for Instagram Story"
                onClick={() =>
                  copyToClipboard("Link copied to clipboard! Open Instagram, go to your Story, and paste the link.")
                }
                className="p-0 m-3"
                style={{ background: "transparent", border: "none", cursor: "pointer" }}
              >
                <FaInstagram size={SHARE_ICON_SIZE} color="#E4405F" />
              </Button>

              <FacebookShareButton
                url={productUrl}
                title={product?.name}
                hashtag="#Wallscapes"
                className="m-3"
              >
                <FaFacebook title="Share on Facebook" size={SHARE_ICON_SIZE} color="#1877F2" />
              </FacebookShareButton>

              <TwitterShareButton
                url={productUrl}
                title={product?.name}
                via="WallscapesAI"
                hashtags={["Wallscapes", "Decor"]}
                related={["Wallscapes"]}
                className="m-3"
              >
                <FaXTwitter title="Share on X (Twitter)" size={SHARE_ICON_SIZE} color="black" />
              </TwitterShareButton>

              <PinterestShareButton
                url={productUrl}
                media={productImgUrl}
                description={`${product?.name} - ${product?.description}`}
                className="m-3"
              >
                <FaPinterest title="Share on Pinterest" size={SHARE_ICON_SIZE} color="#E60023" />
              </PinterestShareButton>

              {/* Custom Email Share Button using mailto */}
              <a href={mailtoLink} title="Share via Email" className="m-0">
                <Button
                  className="p-0 m-3"
                  style={{ background: "transparent", border: "none" }}>
                  <FaEnvelope size={SHARE_ICON_SIZE} color="gray" />
                </Button>
              </a>

              {/* Clipboard Copy Button with Toast */}
              <Button
                title="Copy Link"
                onClick={() => copyToClipboard("Link copied to clipboard!")}
                className="p-0 m-3"
                style={{ background: "transparent", border: "none", cursor: "pointer" }}
              >
                <FaRegCopy size={SHARE_ICON_SIZE} color="gray" />
              </Button>
            </div>

            <div className="mt-3" dangerouslySetInnerHTML={{ __html: product?.catalogItemDescription || '' }} />

            {product?.prompt && (
              <div className="mt-3">
                <h6>Prompt</h6>
                <p style={{ whiteSpace: 'pre-wrap' }}>
                  {getTruncatedPrompt()}
                  {product.prompt.length > 250 && (
                    <Button
                      size="sm"
                      variant="link"
                      onClick={() => setShowFullPrompt(!showFullPrompt)}
                    >
                      {showFullPrompt ? "Show Less" : "More..."}
                    </Button>
                  )}
                </p>
                {product.sourceImageId && (
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={handleRemixProduct}
                    title="Remix this product with additional prompt"
                  >
                    <FaPalette className="me-1" /> Remix This Product
                  </Button>
                )}
              </div>
            )}

            <hr />
            <h5>Put this image on a different product</h5>
            {isAuthenticated ? (
              <>
                <Row className="my-3">
                  <Col>
                    <p>Select a different product type to use with the same image:</p>

                    {loading && <Spinner size="sm" />}<br />
                    {error && <p className="text-danger">{error}</p>}
                    {!loading && catalogItems.length === 0 && <p>No other product types found.</p>}

                    <CatalogItemSelector
                      catalogItems={catalogItems}
                      selectedCatalogItemId={selectedCatalogItemId}
                      selectedVariantId={selectedVariantId}
                      onSelectItem={setSelectedCatalogItemId}
                      onSelectVariant={handleSelectVariant}
                      excludeCatalogItemId={product.catalogItemId}
                      excludeCatalogItemVariantId={product.catalogItemVariantId}
                    />
                  </Col>
                </Row>
                <Row className="align-items-center my-3">
                  <Col>
                    {product.sourceImageId && (
                      <Button
                        variant="secondary"
                        className="w-100 mt-2 mt-md-0"
                        onClick={handleCreateDifferentProduct}
                        disabled={!selectedCatalogItemId || !selectedVariantId || loadingState === LoadingStates.LOADING}
                      >
                        {loadingState === LoadingStates.LOADING ? <Spinner size="sm" /> : "Create New Product"}
                      </Button>
                    )}
                  </Col>
                </Row>
              </>
            ) : (
              <>
                <p className="text-muted">Log in to create a new product with this image.</p>
                <Button
                  onClick={() => {
                    loginWithRedirect();
                  }}
                  variant="primary"
                >
                  Login or Sign up
                </Button>
              </>
            )}

          </Col>
        </Row>

        {/* Error Loading Display */}
        {loadingState === LoadingStates.ERROR && (
          <Row className="justify-content-center mt-3">
            <Col xs={12} md={10}>
              <Loading state={loadingState} message={loadingMessage} />
            </Col>
          </Row>
        )}
      </Container>
    </>
  );
};

export default ProductDetail;
